import { getApiUrl } from '../config/apiUrl';
import {
  callApi,
  callApiInit,
  callApiLogin,
  getTokenView,
  descryptedaes256cbc,
} from './utils';
import { getCompanySlugFromUrl } from '../utils/companyDetector';

/**
 * The function `getToken` retrieves an access token from local storage, decrypts it if a certain
 * condition is met, and returns the access token.
 * @returns the `access_token` property of the `token` object.
 */
function getToken() {
  let token = '';
  try {
    const ceaData = localStorage.getItem('cea');
    if (!ceaData) {
      return token;
    }

    // Primero intentar desencriptar
    const localToken = getTokenView(localStorage.getItem('session'));
    if (localToken) {
      try {
        const decrypted = descryptedaes256cbc(ceaData, localToken);
        if (decrypted && decrypted.access_token) {
          return decrypted.access_token;
        }
      } catch (decryptError) {
        // Si falla desencriptar, intentar JSON (compatibilidad hacia atrás)
        try {
          const tokenData = JSON.parse(ceaData);
          if (tokenData && tokenData.access_token) {
            return tokenData.access_token;
          }
        } catch (jsonError) {
          console.error("Error obteniendo token:", jsonError);
        }
      }
    } else {
      // Si no hay localToken, intentar JSON directamente
      try {
        const tokenData = JSON.parse(ceaData);
        if (tokenData && tokenData.access_token) {
          return tokenData.access_token;
        }
      } catch (jsonError) {
        console.error("Error obteniendo token:", jsonError);
      }
    }
  } catch (error) {
    console.error("Error obteniendo token:", error);
  }
  return token;
}

//VERIFY TOKEN
export const apiGetVerifyToken = async () => {
  const url = `${getApiUrl()}/api/v1/users/verify`;
  const json = await callApi(url, 'GET', getToken(), null);
  // console.log('json', json);
  return json;
};

//SESSION TOKEN
export const apiGetSession = async () => {
  const url = `${getApiUrl()}/api/v3/auth/init`;
  const json = await callApiInit(url, 'GET', null, null);
  return json;
};

//LOGIN
export const apiLogin = async (username, password) => {
  const url = `${getApiUrl()}/api/v3/auth/signin`;
  const body = {
    username,
    password,
  };
  
  // Obtener el slug de la URL para validar que el usuario pertenece a la empresa correcta
  // Intentar múltiples veces para asegurar que se detecte correctamente
  let slug = getCompanySlugFromUrl();
  
  // Si no se detectó slug y estamos en app.hospedapp.cl, intentar desde localStorage
  // (útil cuando el usuario accede directamente a /auth/login)
  if (!slug && (window.location.hostname === 'app.hospedapp.cl' || window.location.hostname.includes('app.hospedapp.cl'))) {
    const storedSlug = localStorage.getItem('detectedCompanySlug');
    if (storedSlug) {
      slug = storedSlug;
    }
  }
  
  const headers = {};
  if (slug) {
    headers['X-Company-Slug'] = slug;
    // Guardar el slug para referencia futura
    localStorage.setItem('detectedCompanySlug', slug);
  }
  
  const json = await callApiLogin(url, 'POST', null, body, headers);
  return json;
};

//LOGOUT
export const apiLogout = async (refreshToken) => {
  const url = `${getApiUrl()}/api/v3/auth/logout`;
  const body = {
    refresh_token: refreshToken,
  };
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//REFRESH TOKEN
export const apiRefreshToken = async (refreshToken) => {
  const url = `${getApiUrl()}/api/v3/auth/refresh`;
  const body = {
    refresh_token: refreshToken,
  };
  const json = await callApi(url, 'POST', null, body);
  return json;
};

//RECOVERY PASSWORD
export const apiRecoveryPassword = async (emailOrBody) => {
  const url = `${getApiUrl()}/api/v3/auth/recovery`;
  
  // Aceptar tanto string como objeto
  let body;
  if (typeof emailOrBody === 'string') {
    body = { email: emailOrBody };
  } else if (emailOrBody && typeof emailOrBody === 'object') {
    body = {
      email: emailOrBody.email,
      captchaToken: emailOrBody.captchaToken || emailOrBody.captcha,
    };
  } else {
    body = emailOrBody || {};
  }
  
  const json = await callApi(url, 'POST', null, body);
  return json;
};

//GET ALL USERS
export const apiGetAllUsers = async () => {
  const url = `${getApiUrl()}/api/v1/users/list`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET USER BY ID
export const apiGetUserById = async (id) => {
  const url = `${getApiUrl()}/api/v1/users/list/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//CREATE USER
export const apiCreateUser = async (body) => {
  const url = `${getApiUrl()}/api/v1/users/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//UPDATE USER
export const apiUpdateUser = async (id, body) => {
  const url = `${getApiUrl()}/api/v1/users/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), body);
  return json;
};

//CHANGE PASSWORD (usuario actual)
export const apiChangePassword = async (body) => {
  const url = `${getApiUrl()}/api/v1/users/change-password`;
  const json = await callApi(url, 'PUT', getToken(), body);
  return json;
};

//DELETE USER
export const apiDeleteUser = async (body) => {
  const url = `${getApiUrl()}/api/v1/users/delete`;
  const json = await callApi(url, 'DELETE', getToken(), body);
  return json;
};

//GET USERS USAGE (cupo del plan)
export const apiGetUsersUsage = async () => {
  const url = `${getApiUrl()}/api/v1/users/usage`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET ME
export const apiGetMe = async () => {
  const url = `${getApiUrl()}/api/v1/users/get/me`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET ALL ROOMS
// Por defecto solo devuelve las habitaciones activas. `includeInactive` es para
// la pantalla de gestión, que necesita ver las desactivadas para reactivarlas.
export const apiGetAllRooms = async (includeInactive = false) => {
  const url = `${getApiUrl()}/api/v1/rooms/list${includeInactive ? '?includeInactive=true' : ''}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET ROOM BY ID
export const apiGetRoomById = async (id) => {
  const url = `${getApiUrl()}/api/v1/rooms/list/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//CREATE ROOM
export const apiCreateRoom = async (body) => {
  const url = `${getApiUrl()}/api/v1/rooms/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//UPDATE ROOM
export const apiUpdateRoom = async (id, body) => {
  const url = `${getApiUrl()}/api/v1/rooms/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), body);
  return json;
};

//ACTIVAR / DESACTIVAR HABITACIÓN (reemplaza al borrado)
export const apiSetRoomActive = async (id, isActive) => {
  const url = `${getApiUrl()}/api/v1/rooms/active/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), { isActive });
  return json;
};

//USO DE HABITACIONES FRENTE AL LÍMITE DEL PLAN
export const apiGetRoomsUsage = async () => {
  const url = `${getApiUrl()}/api/v1/rooms/usage`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET ALL GUESTS
export const apiGetAllGuests = async () => {
  const url = `${getApiUrl()}/api/v1/guest/list`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET GUEST BY ID
export const apiGetGuestById = async (id) => {
  const url = `${getApiUrl()}/api/v1/guest/list/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//CREATE GUEST WITH BOOKING
export const apiCreateGuest = async (body) => {
  const url = `${getApiUrl()}/api/v1/guest/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//UPDATE GUEST
export const apiUpdateGuest = async (id, body) => {
  const url = `${getApiUrl()}/api/v1/guest/update/${id}`;
  const json = await callApi(url, 'PUT', getToken(), body);
  return json;
};

//DELETE GUEST
export const apiDeleteGuest = async (id) => {
  const url = `${getApiUrl()}/api/v1/guest/delete/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

//FIND GUEST BY RUT
export const apiFindGuestByRut = async (body) => {
  const url = `${getApiUrl()}/api/v1/guest/find-by-rut`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//GET CONFIRMED BOOKINGS
export const apiGetConfirmedBookings = async () => {
  const url = `${getApiUrl()}/api/v1/booking/confirmed`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET ALL BOOKINGS (including completed)
export const apiGetAllBookings = async () => {
  const url = `${getApiUrl()}/api/v1/booking/all`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET AVAILABLE ROOMS
export const apiGetAvailableRooms = async (checkin, checkout) => {
  const url = `${getApiUrl()}/api/v1/booking/availability?checkin=${checkin}&checkout=${checkout}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET CHECKOUTS TODAY
export const apiGetCheckoutsToday = async () => {
  const url = `${getApiUrl()}/api/v1/booking/checkouts/today`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET BOOKING BY ID
export const apiGetBookingById = async (id) => {
  const url = `${getApiUrl()}/api/v1/booking/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//CREATE BOOKING
export const apiCreateBooking = async (body) => {
  const url = `${getApiUrl()}/api/v1/booking/create`;
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

//UPDATE BOOKING
export const apiUpdateBooking = async (id, body) => {
  const url = `${getApiUrl()}/api/v1/booking/update/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

//REPLACE BOOKING OCCUPANTS
export const apiReplaceBookingOccupants = async (id, occupants) => {
  const url = `${getApiUrl()}/api/v1/booking/occupants/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), { occupants });
  return json;
};

//DELETE BOOKING
export const apiDeleteBooking = async (id) => {
  const url = `${getApiUrl()}/api/v1/booking/${id}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

//CHECKOUT BOOKING
export const apiCheckoutBooking = async (id, body) => {
  const url = `${getApiUrl()}/api/v1/booking/checkout/${id}`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

//GET GUEST HISTORY
export const apiGetGuestHistory = async (id) => {
  const url = `${getApiUrl()}/api/v1/booking/guest-history/${id}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET CRON CONFIG
export const apiGetCronConfig = async () => {
  const url = `${getApiUrl()}/api/v1/booking/cron/config`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//UPDATE CRON CONFIG
export const apiUpdateCronConfig = async (body) => {
  const url = `${getApiUrl()}/api/v1/booking/cron/config`;
  const json = await callApi(url, 'PATCH', getToken(), body);
  return json;
};

//GET COMPANY BY SLUG (público, sin autenticación para detección inicial)
export const apiGetCompanyBySlug = async (slug) => {
  const url = `${getApiUrl()}/api/v1/companies/public/slug/${slug}`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('Empresa no encontrada');
    }
    return await response.json();
  } catch (error) {
    console.error('Error obteniendo empresa por slug:', error);
    throw error;
  }
};

/** Branding público del producto (imagen de login, sin autenticación) */
export const apiGetPublicBranding = async () => {
  const url = `${getApiUrl()}/api/v1/branding/public`;
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error('No se pudo obtener el branding');
    }
    return await response.json();
  } catch (error) {
    console.error('Error obteniendo branding público:', error);
    throw error;
  }
};

//GET COMPANY BY ID (requiere autenticación)
export const apiGetCompanyById = async (companyId) => {
  const url = `${getApiUrl()}/api/v1/companies/${companyId}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

/** Preferencias del hospedaje (nomenclatura, checkout, modelo de precio, etc.) */
export const apiUpdateCompanySettings = async (companyId, settings) => {
  const url = `${getApiUrl()}/api/v1/companies/${companyId}/settings`;
  return callApi(url, 'PATCH', getToken(), settings);
};

// ============================================
// ALIAS DE COMPATIBILIDAD (nombres antiguos)
// ============================================

// Alias para nombres antiguos de funciones
export const apiListBookingConfirmed = apiGetConfirmedBookings;
export const apiListRooms = apiGetAllRooms;
export const apiListGuest = apiGetAllGuests;
export const apiListUsers = apiGetAllUsers;
export const apiGuestFindRut = apiFindGuestByRut;
export const apiGuestCreate = apiCreateGuest;
export const apiBookingCreate = apiCreateBooking;
export const apiListGuestHistories = apiGetGuestHistory;
export const apiUpdateGuestData = apiUpdateGuest;
export const apiUpdateUserData = apiUpdateUser;
export const apiPerformCheckout = apiCheckoutBooking;
export const apiListBokingID = apiGetBookingById;
export const getRecoveryPass = apiRecoveryPassword;

// Funciones de autenticación adicionales (compatibilidad)
export const apiRegisterLogin = async (body) => {
  // Alias para registro/login (puede usar apiLogin o implementación específica)
  if (body.username || body.email) {
    return apiLogin(body.username || body.email, body.password);
  }
  // Si viene en formato diferente, intentar adaptarlo
  console.warn('apiRegisterLogin: formato de body no reconocido, usando apiLogin');
  return apiLogin(body.username || body.email || '', body.password || '');
};

export const apiGetLogin = async (body) => {
  // Alias para login que acepta body con username/password o método form
  if (body.method === 'form' && body.username && body.password) {
    return apiLogin(body.username, body.password);
  }
  // Si viene en formato estándar
  return apiLogin(body.username || body.email || '', body.password || '');
};

export const apiVerifyTokenRecovery = async (body) => {
  // Verificar token de recuperación
  // body puede ser { token: "..." } o directamente el token string
  const token = body.token || body;
  
  // Nota: Este endpoint puede no existir en el backend actual
  // Si el backend no tiene este endpoint, retornar un objeto con isExpired: false
  try {
    const url = `${getApiUrl()}/api/v3/auth/verify-recovery/${token}`;
    const response = await fetch(url);
    if (!response.ok) {
      // Si el endpoint no existe, retornar estructura esperada
      return { isExpired: true, email: null };
    }
    return await response.json();
  } catch (error) {
    console.warn('Endpoint de verificación de token no disponible:', error);
    // Retornar estructura esperada por el componente
    return { isExpired: true, email: null };
  }
};

export const apiUpdatePass = async (body) => {
  // Actualizar contraseña con token de recuperación (endpoint público)
  try {
    const url = `${getApiUrl()}/api/v3/auth/reset-password`;
    const requestBody = {
      token: body.token || (typeof body === 'string' ? body : null),
      newPassword: body.newpass || body.newPassword || body.password,
      renewPassword: body.renewpass || body.renewPassword,
    };

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    const json = await response.json().catch(() => ({}));
    if (!response.ok) {
      return {
        result: false,
        message:
          json?.message ||
          (Array.isArray(json?.message) ? json.message.join(', ') : null) ||
          'Error al actualizar contraseña',
      };
    }

    return {
      result: json.result !== false,
      message: json.message || 'Contraseña actualizada',
    };
  } catch (error) {
    console.error('Error actualizando contraseña:', error);
    return { result: false, message: error.message || 'Error al actualizar contraseña' };
  }
};

// Subscription API
export const apiGetMySubscription = async () => {
  const url = `${getApiUrl()}/api/v1/subscriptions/my-subscription`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

export const apiCreateMercadoPagoCheckout = async (plan, periodicity = 'monthly') => {
  const url = `${getApiUrl()}/api/v1/mercadopago/checkout`;
  const body = { plan, periodicity };
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

/** Headers de empresa para flujos de renovación sin JWT. */
const buildRenewalHeaders = () => {
  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  let slug = getCompanySlugFromUrl();
  if (
    !slug &&
    (window.location.hostname === 'app.hospedapp.cl' ||
      window.location.hostname.includes('app.hospedapp.cl'))
  ) {
    const storedSlug = localStorage.getItem('detectedCompanySlug');
    if (storedSlug) slug = storedSlug;
  }
  if (slug) {
    headers['X-Company-Slug'] = slug;
    localStorage.setItem('detectedCompanySlug', slug);
  }
  return headers;
};

/**
 * Valida credenciales de renovación y devuelve habitaciones activas + último plan
 * para filtrar el catálogo antes de ir a Mercado Pago.
 */
export const apiGetMercadoPagoRenewalContext = async (email, password) => {
  const url = `${getApiUrl()}/api/v1/mercadopago/renewal-context`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildRenewalHeaders(),
    body: JSON.stringify({ email, password }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const err = new Error(data?.message || 'No se pudo verificar la cuenta');
    err.status = response.status;
    err.data = data;
    throw err;
  }
  return data;
};

export const apiCreateMercadoPagoCheckoutRenewal = async (
  email,
  password,
  plan,
  periodicity = 'monthly',
) => {
  const url = `${getApiUrl()}/api/v1/mercadopago/checkout-renewal`;
  const body = { email, password, plan, periodicity };

  const response = await fetch(url, {
    method: 'POST',
    headers: buildRenewalHeaders(),
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = new Error(data?.message || 'No se pudo iniciar el pago');
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return data;
};

export const apiConfirmMercadoPagoPayment = async (paymentId) => {
  const url = `${getApiUrl()}/api/v1/mercadopago/confirm`;
  const body = { paymentId: String(paymentId) };
  const json = await callApi(url, 'POST', getToken(), body);
  return json;
};

const MP_RENEWAL_AUTH_KEY = 'hospedapp_mp_renewal_auth';

/** Guarda token de renovación (JWT corto del API), no la contraseña. */
export const storeMercadoPagoRenewalAuth = (renewalToken) => {
  try {
    sessionStorage.setItem(
      MP_RENEWAL_AUTH_KEY,
      JSON.stringify({ renewalToken, ts: Date.now() }),
    );
  } catch {
    /* sessionStorage no disponible */
  }
};

export const clearMercadoPagoRenewalAuth = () => {
  try {
    sessionStorage.removeItem(MP_RENEWAL_AUTH_KEY);
  } catch {
    /* ignore */
  }
};

export const apiConfirmMercadoPagoPaymentRenewal = async (paymentId) => {
  const raw = sessionStorage.getItem(MP_RENEWAL_AUTH_KEY);
  if (!raw) {
    throw new Error(
      'No hay credenciales de renovación guardadas. Vuelve a iniciar el pago desde "Renovar suscripción".',
    );
  }

  let auth;
  try {
    auth = JSON.parse(raw);
  } catch {
    clearMercadoPagoRenewalAuth();
    throw new Error('Sesión de renovación inválida. Intenta renovar de nuevo.');
  }

  const maxAgeMs = 30 * 60 * 1000;
  const renewalToken = auth?.renewalToken || auth?.token;
  if (!renewalToken || Date.now() - (auth.ts || 0) > maxAgeMs) {
    clearMercadoPagoRenewalAuth();
    throw new Error('La sesión de renovación expiró. Vuelve a "Renovar suscripción".');
  }

  const url = `${getApiUrl()}/api/v1/mercadopago/confirm-renewal`;
  const body = {
    renewalToken,
    paymentId: String(paymentId),
  };

  let slug = getCompanySlugFromUrl();
  if (
    !slug &&
    (window.location.hostname === 'app.hospedapp.cl' ||
      window.location.hostname.includes('app.hospedapp.cl'))
  ) {
    const storedSlug = localStorage.getItem('detectedCompanySlug');
    if (storedSlug) slug = storedSlug;
  }

  const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
  if (slug) {
    headers['X-Company-Slug'] = slug;
    localStorage.setItem('detectedCompanySlug', slug);
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const err = new Error(data?.message || 'No se pudo confirmar el pago');
    err.status = response.status;
    err.data = data;
    throw err;
  }

  clearMercadoPagoRenewalAuth();
  return data;
};

export const apiGetMercadoPagoConfig = async (signal) => {
  const apiUrl = getApiUrl();
  if (!apiUrl) {
    throw new Error(
      'La URL de la API no está configurada en el portal. Contacta al administrador.',
    );
  }
  const url = `${apiUrl}/api/v1/mercadopago/config`;
  let response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal,
    });
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error(
      'No se pudo conectar con el servidor para verificar Mercado Pago. Revisa tu conexión.',
    );
  }
  if (!response.ok) {
    throw new Error(
      `No se pudo obtener la configuración de Mercado Pago (${response.status})`,
    );
  }
  return response.json();
};

//GET PURCHASABLE PLANS (catálogo vigente; no requiere sesión porque la pantalla
//de renovación se usa con la suscripción ya vencida)
export const apiGetPurchasablePlans = async (signal) => {
  const apiUrl = getApiUrl();
  if (!apiUrl) {
    throw new Error('La URL de la API no está configurada en el portal.');
  }
  const response = await fetch(`${apiUrl}/api/v1/plans/purchasable`, {
    method: 'GET',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (!response.ok) {
    throw new Error(`No se pudo obtener el catálogo de planes (${response.status})`);
  }
  return response.json();
};

//GET COMPANY STATISTICS (obtiene estadísticas de la empresa del usuario autenticado)
export const apiGetCompanyStatistics = async () => {
  const url = `${getApiUrl()}/api/v1/companies/statistics/my-company`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

// ============================================
// NOTIFICATIONS API
// ============================================

//GET ALL NOTIFICATIONS
export const apiListNotifications = async (companyId = null, unreadOnly = false) => {
  let url = `${getApiUrl()}/api/v1/notifications`;
  const params = [];
  if (companyId) params.push(`companyId=${companyId}`);
  if (unreadOnly) params.push(`unreadOnly=true`);
  if (params.length > 0) url += `?${params.join('&')}`;
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//GET UNREAD NOTIFICATION COUNT
export const apiGetUnreadNotificationCount = async (companyId = null) => {
  let url = `${getApiUrl()}/api/v1/notifications/unread/count`;
  // Solo agregar companyId si es un número válido
  if (companyId !== null && companyId !== undefined && companyId !== '' && !isNaN(Number(companyId))) {
    url += `?companyId=${companyId}`;
  }
  const json = await callApi(url, 'GET', getToken(), null);
  return json;
};

//MARK NOTIFICATION AS READ
export const apiMarkNotificationAsRead = async (notificationId) => {
  const url = `${getApiUrl()}/api/v1/notifications/${notificationId}/read`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

//MARK ALL NOTIFICATIONS AS READ
export const apiMarkAllNotificationsAsRead = async (companyId = null) => {
  let url = `${getApiUrl()}/api/v1/notifications/read-all`;
  if (companyId) url += `?companyId=${companyId}`;
  const json = await callApi(url, 'PATCH', getToken(), null);
  return json;
};

//DELETE NOTIFICATION
export const apiDeleteNotification = async (notificationId) => {
  const url = `${getApiUrl()}/api/v1/notifications/${notificationId}`;
  const json = await callApi(url, 'DELETE', getToken(), null);
  return json;
};

// ============================================
// NPS API
// ============================================

export const apiGetNpsEligibility = async () => {
  const url = `${getApiUrl()}/api/v1/nps/eligibility`;
  return callApi(url, 'GET', getToken(), null);
};

export const apiSubmitNps = async (score, comment = '') => {
  const url = `${getApiUrl()}/api/v1/nps`;
  const body = { score };
  if (comment && comment.trim()) {
    body.comment = comment.trim();
  }
  return callApi(url, 'POST', getToken(), body);
};

const postNpsBlocked = async (path, body) => {
  const url = `${getApiUrl()}${path}`;
  const response = await fetch(url, {
    method: 'POST',
    headers: buildRenewalHeaders(),
    body: JSON.stringify(body),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const nested = typeof data?.error === 'string' ? (() => {
      try {
        return JSON.parse(data.error);
      } catch (err) {
        return null;
      }
    })() : data?.error;
    const err = new Error(nested?.message || data?.message || 'No se pudo completar la encuesta');
    err.status = response.status;
    err.data = { ...data, message: nested?.message || data?.message };
    throw err;
  }
  return data;
};

export const apiGetNpsBlockedEligibility = async (email, password) =>
  postNpsBlocked('/api/v1/nps/blocked/eligibility', { email, password });

export const apiSubmitNpsBlocked = async (email, password, score, comment = '') => {
  const body = { email, password, score };
  if (comment && comment.trim()) {
    body.comment = comment.trim();
  }
  return postNpsBlocked('/api/v1/nps/blocked', body);
};

export const apiGetNpsPublicInvite = async (token) => {
  const url = `${getApiUrl()}/api/v1/nps/public/${encodeURIComponent(token)}`;
  return callApi(url, 'GET', null, null);
};

export const apiSubmitNpsPublic = async (token, score, comment = '') => {
  const url = `${getApiUrl()}/api/v1/nps/public/${encodeURIComponent(token)}`;
  const body = { score };
  if (comment && comment.trim()) {
    body.comment = comment.trim();
  }
  return callApi(url, 'POST', null, body);
};
